import { SecondaryButton } from '@components/Buttons/SecondaryButton';
import { ComboField } from '@components/Fields/ComboField';
import { DateField } from '@components/Fields/DateField';
import { FloatField } from '@components/Fields/FloatField';
import { StringField } from '@components/Fields/StringField';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { DialogActions, Grid, MenuItem } from '@material-ui/core';
import { EnumOrigemCancelamento, IComboOrigemCancelamento, IContratoCliente } from '@model/ContratoCliente';
import { ICancelarContratoRequest, useContratoClienteService } from '@services/ContratoClienteService';
import { addDays } from '@utils/Functions';
import { yup } from '@validations/Yup';
import { AxiosResponse } from 'axios';
import React from 'react';

export type ContratoClienteCancelarModalProps = {
    contratoCliente: IContratoCliente
    modal: IModalWindow
    origemCancelamentoCombo: IComboOrigemCancelamento
    onContratoCancelado: (response: AxiosResponse<IContratoCliente>) => void
}

export function ContratoClienteCancelarModal ({ contratoCliente, modal, origemCancelamentoCombo, onContratoCancelado }: ContratoClienteCancelarModalProps) {
    const justificativaInputRef = React.createRef<HTMLInputElement>();

    const loading = useLoading();
    const message = useMessage();

    const contratoClienteService = useContratoClienteService();

    const form = useForm<ICancelarContratoRequest>({
        valorFatura: useField(0),
        vencimentoFatura: useField(addDays(new Date(), 10)),
        justificativa: useField(),
        origemCancelamento: useField(EnumOrigemCancelamento.None)
    });

    const { fields } = form;

    const validator = useValidation<ICancelarContratoRequest>({
        justificativa: yup.string().trim().required()
    })

    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = form.getInputValues();

            form.clearErrors();
            validator.validate(values)
                .then(() => {
                    loading.start();
                    contratoClienteService.cancelar(contratoCliente.contratoClienteId, values)
                        .then(response => {
                            message.success("Contrato cancelado com sucesso");
                            modal.close();
                            onContratoCancelado(response);
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop)
                        })
                .catch(form.setErrorsFromYup);
        }
    }, [contratoCliente.contratoClienteId, contratoClienteService, form, loading, message, modal, onContratoCancelado, validator]);

    const loadValorFatura = React.useCallback(() => {
        contratoClienteService.obterValorFaturaCancelamento(contratoCliente.contratoClienteId)
            .then(response => {
                form.fields.valorFatura.setValue(response.data);
            })
            .catch (message.showException)
            .finally(loading.stop);
    }, [contratoCliente.contratoClienteId, contratoClienteService, form.fields.valorFatura, loading.stop, message.showException]);

    const handleEnter = React.useCallback(() => {
        form.reset();
        justificativaInputRef.current?.focus();
        loadValorFatura();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [justificativaInputRef]);    

    return (
        <ModalWindow
            modal={modal}
            size="sm"
            disabled={loading.isLoading}
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle
                title={`Cancelamento do contrato ${contratoCliente.contratoClienteId}`}
            />

            <ModalWindowContent>
                <HorizontalGrid spacing={1}>
                    <Grid item xs={6}>
                        <ComboField 
                            field={fields.origemCancelamento}
                            label="Origem"
                            required
                        >
                            <MenuItem key={0} value="">Todos</MenuItem>
                            {
                                origemCancelamentoCombo.createOptions()
                            }
                        </ComboField>
                    </Grid>


                    <Grid item xs={3}>
                        <FloatField
                            field={fields.valorFatura}
                            label="Valor Fatura"
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <DateField
                            field={fields.vencimentoFatura}
                            label="Vencimento"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <StringField 
                            inputRef={justificativaInputRef}
                            field={fields.justificativa}
                            label="Justificativa"
                            required
                            multiline
                        />
                    </Grid>

                </HorizontalGrid>
            </ModalWindowContent>

            <DialogActions>
                <SecondaryButton onClick={handleConfirm} disabled={loading.isLoading}>Cancelar Contrato</SecondaryButton>
            </DialogActions>
        </ModalWindow>
    )
}